import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getSiteUrl } from '../lib/siteConfig';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';

interface TokenPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  onSuccess: () => void;
}

export const TOKEN_PACKAGES = [
  {
    tokens: 1000000,
    displayTokens: '1M',
    price: 10,
    description: 'Great for getting started',
    popular: false
  },
  {
    tokens: 10000000,
    displayTokens: '10M',
    price: 95,
    description: 'Perfect for small teams',
    popular: false
  },
  {
    tokens: 20000000,
    displayTokens: '20M',
    price: 180,
    description: 'Best for growing businesses',
    popular: true
  },
  {
    tokens: 50000000,
    displayTokens: '50M',
    price: 400,
    description: 'Maximum value for power users',
    popular: false
  }
];

export default function TokenPurchaseModal({ isOpen, onClose, businessId, onSuccess }: TokenPurchaseModalProps) {
  const [selectedPackage, setSelectedPackage] = useState<typeof TOKEN_PACKAGES[0] | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;
  
  // Don't render if no business ID
  if (!businessId) return null;

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setProcessing(true);
    setError(null);

    try {
      console.log(`💳 Purchasing ${selectedPackage.displayTokens} tokens for $${selectedPackage.price}`);

      const base = getSiteUrl().replace(/\/$/, '');
      const path =
        typeof window !== 'undefined' && window.location.pathname
          ? window.location.pathname
          : '/ai-settings';
      const successUrl = `${base}${path}?ai_tokens_checkout=1`;
      const cancelUrl = `${base}${path}?ai_tokens_checkout=cancelled`;

      const { data, error: invokeError } = await supabase.functions.invoke('purchase-ai-tokens', {
        body: {
          businessId,
          tokenAmount: selectedPackage.tokens,
          successUrl,
          cancelUrl,
        },
      });

      const payload = data as {
        success?: boolean;
        error?: string;
        status?: string;
        requiresCheckout?: boolean;
        checkoutUrl?: string;
      } | null;

      if (invokeError || !payload?.success) {
        const msg =
          (await invokeErrorMessage(invokeError, payload)) ||
          payload?.error ||
          'Purchase failed. Please try again.';
        throw new Error(msg);
      }

      // Off-session charge failed (or no card) — open Stripe Checkout
      if (payload.requiresCheckout && payload.checkoutUrl) {
        window.location.href = payload.checkoutUrl;
        return;
      }

      if (payload.status !== 'succeeded') {
        throw new Error('Payment could not be confirmed');
      }

      console.log('✅ Purchase successful!', data);

      alert(`Success! ${selectedPackage.displayTokens} tokens added to your account.`);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Purchase error:', err);
      setError(err.message || 'Purchase failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{
        zIndex: 1000010,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Sparkles className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Purchase AI Tokens</h2>
              <p className="text-sm text-gray-600">Choose a package to continue using AI features</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Token Packages */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {TOKEN_PACKAGES.map((pkg) => (
              <button
                key={pkg.tokens}
                onClick={() => setSelectedPackage(pkg)}
                disabled={processing}
                className={`relative p-6 rounded-xl border-2 transition-all ${
                  selectedPackage?.tokens === pkg.tokens
                    ? 'border-blue-600 bg-blue-50 shadow-lg scale-105'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {/* Popular Badge */}
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    MOST POPULAR
                  </div>
                )}

                {/* Selected Checkmark */}
                {selectedPackage?.tokens === pkg.tokens && (
                  <div className="absolute top-4 right-4 bg-blue-600 text-white rounded-full p-1">
                    <Check className="h-4 w-4" />
                  </div>
                )}

                <div className="text-center space-y-3">
                  {/* Token Amount */}
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{pkg.displayTokens}</div>
                    <div className="text-sm text-gray-600">tokens</div>
                  </div>

                  {/* Price */}
                  <div className="text-2xl font-bold text-blue-600">${pkg.price}</div>

                  {/* Description */}
                  <p className="text-sm text-gray-600">{pkg.description}</p>

                  {/* Value Indicator */}
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      ${(pkg.price / (pkg.tokens / 1000000)).toFixed(2)} per 1M tokens
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Usage Examples */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">What can you do with tokens?</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>Generate ~2,000 emails with 1M tokens</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>Edit text ~5,000 times with 1M tokens</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>Tokens never expire and are shared across your team</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={processing}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePurchase}
            disabled={!selectedPackage || processing}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Processing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Purchase {selectedPackage?.displayTokens} Tokens
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
